<template>
  <div class="link-kazang-modal">
    <div class="modal-overlay" @click="$emit('close')"></div>
    <div class="modal-content">
      <div class="modal-header">
        <h4><b>Link Invoice to Payment</b></h4>
        <button class="close-btn" @click="$emit('close')">&times;</button>
      </div>

      <div class="modal-body">
        <p class="info-text">
          Use this to recover Kazang payments where money was deducted but the transaction failed to complete.
        </p>

        <!-- Search Button -->
        <div class="search-section">
          <button
            class="lyt-soft-btn search-btn"
            @click="searchPending"
            :disabled="loading"
          >
            <font-awesome-icon icon="fa-solid fa-search" v-if="!loading"/>
            <font-awesome-icon icon="fa-solid fa-spinner" spin v-if="loading"/>
            {{ loading ? 'Searching...' : 'Search Pending Transactions' }}
          </button>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <!-- Pending Transactions Table -->
        <div v-if="pendingTransactions.length > 0" class="transactions-table">
          <table>
            <thead>
              <tr>
                <th>Transaction ID</th>
                <th>Phone</th>
                <th>Amount</th>
                <th>Network</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tx in pendingTransactions" :key="tx.localID">
                <td>{{ tx.localID }}</td>
                <td>{{ tx.phone || 'N/A' }}</td>
                <td>{{ formatMoney(tx.amount) }}</td>
                <td>{{ tx.network }}</td>
                <td>{{ formatDate(tx.date) }}</td>
                <td>
                  <button
                    class="process-btn"
                    @click="processTransaction(tx)"
                    :disabled="processing === tx.localID"
                  >
                    {{ processing === tx.localID ? 'Processing...' : 'Process' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- No Results -->
        <div v-else-if="searched && !loading && !error" class="no-results">
          No pending transactions found.
        </div>

        <!-- Process Result -->
        <div v-if="processResult" class="process-result" :class="processResult.success ? 'success' : 'error'">
          <p v-if="processResult.success">
            <font-awesome-icon icon="fa-solid fa-check-circle"/>
            Transaction completed successfully!
          </p>
          <p v-else>
            <font-awesome-icon icon="fa-solid fa-exclamation-circle"/>
            {{ processResult.message }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LinkKazangPayment',
  data() {
    return {
      loading: false,
      processing: null,
      searched: false,
      error: null,
      pendingTransactions: [],
      processResult: null
    };
  },
  methods: {
    async searchPending() {
      console.log('[LinkKazangPayment] Search button clicked');
      this.loading = true;
      this.error = null;
      this.searched = true;
      this.pendingTransactions = [];
      this.processResult = null;

      try {
        console.log('[LinkKazangPayment] Dispatching getPendingKazangTransactions');
        const transactions = await this.$store.dispatch('getPendingKazangTransactions');
        console.log('[LinkKazangPayment] Got transactions:', transactions);
        this.pendingTransactions = transactions || [];
      } catch (err) {
        console.error('[LinkKazangPayment] Error:', err);
        this.error = err.message || 'Failed to search pending transactions';
      } finally {
        this.loading = false;
      }
    },
    async processTransaction(tx) {
      this.processing = tx.localID;
      this.processResult = null;

      try {
        const result = await this.$store.dispatch('processPendingKazangTransaction', tx.localID);

        if (result.approved) {
          this.processResult = {
            success: true,
            message: 'Transaction completed successfully!'
          };
          // Remove from list after successful processing
          this.pendingTransactions = this.pendingTransactions.filter(t => t.localID !== tx.localID);
        } else {
          this.processResult = {
            success: false,
            message: result.print || 'Transaction was not approved'
          };
        }
      } catch (err) {
        this.processResult = {
          success: false,
          message: err.message || 'Failed to process transaction'
        };
      } finally {
        this.processing = null;
      }
    },
    formatMoney(amount) {
      if (!amount) return 'ZMW 0.00';
      return `ZMW ${parseFloat(amount).toFixed(2)}`;
    },
    formatDate(dateStr) {
      if (!dateStr) return 'N/A';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } catch (e) {
        return dateStr;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.link-kazang-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
}

.modal-content {
  position: relative;
  background: var(--card-bg, #fff);
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--card-border, #e0e0e0);

  h4 {
    margin: 0;
    color: var(--text-primary, #333);
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: var(--text-primary, #333);
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: #f00;
    }
  }
}

.modal-body {
  padding: 20px;
}

.info-text {
  color: var(--text-secondary, #666);
  margin-bottom: 20px;
  font-size: 14px;
}

.search-section {
  margin-bottom: 20px;
}

.search-btn {
  width: 100%;
  max-width: 300px;
}

.error-message {
  background: #fee;
  color: #c00;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.no-results {
  text-align: center;
  color: var(--text-secondary, #666);
  padding: 40px;
}

.transactions-table {
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;

    th, td {
      padding: 12px;
      text-align: left;
      border-bottom: 1px solid var(--card-border, #e0e0e0);
    }

    th {
      background: var(--bg-secondary, #f5f5f5);
      font-weight: 600;
      color: var(--text-primary, #333);
    }

    td {
      color: var(--text-primary, #333);
    }
  }
}

.process-btn {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #45a049, #3d8b40);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.process-result {
  margin-top: 20px;
  padding: 16px;
  border-radius: 8px;
  text-align: center;

  &.success {
    background: #efe;
    color: #080;
  }

  &.error {
    background: #fee;
    color: #c00;
  }

  p {
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
}

.lyt-soft-btn {
  color: white;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  height: 50px;
  background: linear-gradient(135deg, var(--accent-electric-blue, #2196F3), var(--accent-neon-purple, #9C27B0));
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: transform 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
